/**
 * Per-developer metrics, for the Developer League and the developer list.
 *
 * A note on framing, because it constrains the numbers: our product philosophy
 * is explicit that individual metrics are for debugging and 1:1 preparation,
 * never for ranking people. So the fixture is shaped to make a TEAM finding
 * legible - Priya and Marcus carry most of the review load, Dmitri carries
 * payments alone - rather than to produce a leaderboard with a loser at the
 * bottom. Everyone here is plausibly contributing; the interesting signal is
 * the imbalance, which is a management problem, not a person problem.
 */

import type { DeveloperWithMetrics, MetricValue } from '@/types/core';
import type { BulkDeveloperMetricsResponse } from '@/lib/api/developer';
import {
  DEMO_ORG_ID,
  DEMO_REPOS,
  findDemoRepo,
  resolveContributors,
  resolveProfile,
  rollUpProfile,
  type DemoDeveloper,
} from '../fixture';
import { demoDate, jitter, rngFor, type DemoSeedInput } from '../seed';

export interface DemoDeveloperParams {
  days: number;
  repoId?: string | null;
  teamId?: string | null;
  excludeBots: boolean;
}

const seedInput = (params: DemoDeveloperParams, route: string): DemoSeedInput => ({
  route,
  days: params.days,
  repoId: params.repoId,
  teamId: params.teamId,
  excludeBots: params.excludeBots,
});

function metric(value: number, params: DemoDeveloperParams): MetricValue {
  return {
    value: Number(value.toFixed(2)),
    period_start: demoDate(params.days),
    period_end: demoDate(0),
  };
}

/**
 * The profile a developer's own numbers should come from.
 *
 * Not the org roll-up. Using the roll-up for everyone left the bus-factor owner
 * of the slowest, churniest repo looking CLEANER than the flagship developer -
 * churn 10.7 vs 12.1, revert 1.59 vs 1.77 - because the only thing separating
 * two people was jitter. That makes the drilldown contradict the story the
 * dashboard just told, which is worse than having no drilldown.
 *
 * When the page is scoped to a repo, that repo wins: the question being asked
 * is "what does this person look like HERE".
 */
function developerProfile(dev: DemoDeveloper, params: DemoDeveloperParams) {
  const scoped = findDemoRepo(params.repoId);
  if (scoped) return scoped.profile;

  const theirs = DEMO_REPOS.filter((r) => r.profile.contributorIds.includes(dev.id));
  return theirs.length > 0 ? rollUpProfile(theirs) : resolveProfile(params.repoId, params.teamId);
}

function metricsFor(
  dev: DemoDeveloper,
  params: DemoDeveloperParams,
  totalOutputWeight: number,
  totalReviewWeight: number
): Record<string, MetricValue> {
  const profile = developerProfile(dev, params);
  // Volume still scales off the scope's profile, so the per-developer numbers
  // continue to sum to the cohort totals shown above the table.
  const scopeProfile = resolveProfile(params.repoId, params.teamId);
  const rng = rngFor(seedInput(params, 'developer/metrics'), dev.id);

  const outputShare = dev.outputWeight / totalOutputWeight;
  const reviewShare = totalReviewWeight > 0 ? dev.reviewGiveWeight / totalReviewWeight : 0;
  const windowPrs = scopeProfile.prsPerDay * params.days * 0.92;

  const prsMerged = Math.max(0, Math.round(windowPrs * outputShare * jitter(rng, 0.12)));
  const reviewsGiven = Math.max(0, Math.round(windowPrs * reviewShare * 1.6 * jitter(rng, 0.14)));

  const avgPrSize = profile.avgPrSize * jitter(rng, 0.25);
  const commitsPerPr = 3.1 * jitter(rng, 0.2);
  const cycleTime = profile.cycleTimeHours * jitter(rng, 0.22);

  return {
    prs_merged: metric(prsMerged, params),
    prs_created: metric(Math.round(prsMerged * 1.12), params),
    prs_reviewed: metric(reviewsGiven, params),
    reviews_given: metric(reviewsGiven, params),
    // The ratio that makes the imbalance legible: well above 1 means this
    // person reviews far more than they ship, which is the burden signal.
    review_load_ratio: metric(prsMerged > 0 ? reviewsGiven / prsMerged : reviewsGiven, params),
    reviewer_diversity_count: metric(Math.max(1, Math.round(people(params).length * 0.45 * jitter(rng, 0.2))), params),
    prs_merged_without_approval: metric(Math.round(prsMerged * (1 - profile.reviewCoverageBase / 100)), params),
    cycle_time_hours: metric(cycleTime, params),
    median_cycle_time_hours: metric(cycleTime * 0.82, params),
    p95_cycle_time_hours: metric(cycleTime * 2.6, params),
    avg_time_to_first_review_hours: metric(cycleTime * profile.waitForReviewShare, params),
    avg_time_to_approval_hours: metric(cycleTime * (profile.waitForReviewShare + 0.18), params),
    avg_review_response_time_hours: metric(cycleTime * 0.24 * jitter(rng, 0.3), params),
    avg_pr_size: metric(avgPrSize, params),
    // The four cards that rendered "-" until these existed. Derived from
    // avgPrSize rather than drawn separately, so "Avg Lines / PR" times
    // "PRs Merged" still reconciles with the lines-of-code total on the page.
    avg_lines_per_pr: metric(avgPrSize, params),
    avg_lines_per_commit: metric(avgPrSize / commitsPerPr, params),
    avg_files_per_pr: metric(Math.max(1, avgPrSize / 46) * jitter(rng, 0.2), params),
    code_churn_rate_percent: metric(Math.min(60, 11 * profile.churnMultiplier * jitter(rng, 0.2)), params),
    loc_per_day: metric(outputShare * 620 * jitter(rng, 0.12), params),
    loc_per_day_with_branches: metric(outputShare * 620 * 1.18 * jitter(rng, 0.12), params),
    commits_per_day: metric(outputShare * 14 * jitter(rng, 0.15), params),
    test_pass_rate: metric(Math.min(100, (100 - profile.testFailureRateBase) * jitter(rng, 0.04)), params),
    test_pass_rate_percent: metric(Math.min(100, (100 - profile.testFailureRateBase) * jitter(rng, 0.04)), params),
    // Contributor Reliability reads these three; without them the card renders
    // "Not enough data" on a profile that plainly has plenty.
    revert_rate_percent: metric(Math.min(12, 1.6 * profile.churnMultiplier * jitter(rng, 0.3)), params),
    fix_followthrough_percent: metric(Math.min(100, 88 * jitter(rng, 0.06)), params),
    ci_recovery_hours: metric(2.4 * profile.churnMultiplier * jitter(rng, 0.25), params),
  };
}

/** Cohort in scope, used for reviewer-diversity scaling. */
function people(params: DemoDeveloperParams) {
  return resolveContributors(params.repoId, params.teamId, params.excludeBots);
}

export function buildDemoDeveloperMetrics(
  params: DemoDeveloperParams
): BulkDeveloperMetricsResponse {
  const people = resolveContributors(params.repoId, params.teamId, params.excludeBots);
  const totalOutputWeight = people.reduce((s, d) => s + d.outputWeight, 0) || 1;
  const totalReviewWeight = people.reduce((s, d) => s + d.reviewGiveWeight, 0) || 1;
  const rng = rngFor(seedInput(params, 'developer/open-prs'), 'backlog');

  const developers: DeveloperWithMetrics[] = people.map((dev) => ({
    id: dev.id,
    organization_id: DEMO_ORG_ID,
    external_id: `demo-${dev.id}`,
    external_login: dev.login,
    name: dev.name,
    last_active_at: '2026-09-01T00:00:00Z',
    created_at: '2024-02-05T00:00:00Z',
    updated_at: '2026-09-01T00:00:00Z',
    metrics: metricsFor(dev, params, totalOutputWeight, totalReviewWeight),
  }));

  const openPrsTotal = Math.max(1, Math.round(people.length * 1.7 * jitter(rng, 0.2)));

  return {
    developers,
    has_more: false,
    total: developers.length,
    open_prs_total: openPrsTotal,
    active_developers: developers.length,
    open_prs_per_active_dev:
      developers.length > 0 ? Number((openPrsTotal / developers.length).toFixed(2)) : null,
  };
}
